
import {retry,  withLatestFrom, exhaustMap, switchMap, debounceTime, map, filter, tap, share, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { NgZone, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { AppState } from '../../interfaces/app-state.interface';
import { ErrorsType } from '../../enum/errors.type';
import { RoutesTable } from '../../resources/routes-table.model';
import { BackendService } from '../../services/backend/backend.service';
import { TrackingService } from '../../services/tracking/tracking.service';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { TrackingActions } from './../tracking/tracking.action';
import { SuitabilityAction } from '../suitability/suitability.action';
import { UserProfileActions } from './userprofile.actions';
import { PersonTypeEnum } from '../../enum/person-type.enum';
import { getProfileDisplayName } from '../../resources/profile.utils';
import { UserProfileService } from './../../services/users/user-profile.service';
import { BackendService as NewBackendService} from '../../services/new-http/backend.service';

/**
 * Aplica personType PF/PJ ao perfil quando o backend devolve Indefinido (0).
 * Centraliza a regra de derivacao por tamanho do documento.
 */
function withDerivedPersonType(profile: any): any {
    if (!profile) return profile;
    return {
        ...profile,
        personType: String(profile.cpfcnpj || '').length > 11 ? PersonTypeEnum.pj : PersonTypeEnum.pf,
    };
}

@Injectable()
export class UserProfileEffects {

    /**
     * Atualiza um usuário.
     * @method updateUser
     */
    updateUser$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.update),
            withLatestFrom(this.store.select((state) => state.userProfile)),
            switchMap(([action, store]: [any, any]) => {
                return this.userService.updateUser({ step: action.payload.step, value: action.payload.value })
                    .pipe(
                        map((result) => {
                            if (result.ok || result.hasValue) {
                                const payload = result.hasValue ? result.value : { ...store.user, ...action.payload.value };
                                return { type: UserProfileActions.updateSuccess, payload };
                            }

                            if (result.hasError) {
                                return { type: UserProfileActions.updateError, payload: { type: ErrorsType.Error, value: result.messages } };
                            }

                            if (result.hasAlerta || result.hasWarning) {
                                return { type: UserProfileActions.updateError, payload: { type: ErrorsType.Warning, value: result.messages } };
                            }

                            return { type: UserProfileActions.updateSuccess, payload: result };

                        }),
                        catchError((error) => of({ type: UserProfileActions.updateError, payload: error })));
            }),
            share()));

    /**
     * Atualiza parte de um usuário.
     * @method updatePatchUser
     */
    updatePatchUser$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.updatePatch),
            withLatestFrom(this.store.select((state) => state.userProfile)),
            exhaustMap(([action, store]: [any, any]) => {

                if (action.payload.isVirtualOnly) {
                    return of({
                        type: UserProfileActions.updateVirtualUser,
                        payload: { ...store.virtualUser, ...action.payload.value },
                    });
                }

                return this.userService.updatePatchUser(action.payload)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: UserProfileActions.updatePatchSuccess, payload: { ...store.user, ...action.payload.value } };
                            }

                            return { type: UserProfileActions.updatePatchError, payload: { type: ErrorsType.Warning, value: result.messages } };

                        }),
                        catchError((error) => {
                            return of({ type: UserProfileActions.updatePatchError, payload: error });
                        }));
            }),
            share()));

    /**
     * Atualiza parte de um usuário.
     * @method updateUserTags
     */
    updateUserTags$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.updateTags),
            switchMap((action: any) => {
                return this.userService.updateUserTags(action.payload)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: UserProfileActions.updateTagsSuccess, payload: { userTag: action.payload.value } };
                            }

                            return { type: UserProfileActions.updateTagsError, payload: { type: ErrorsType.Warning, value: result.messages } };
                        }),
                        catchError((error) => of({ type: UserProfileActions.updateTagsError, payload: error })),
                    ).pipe(retry(3));
            }),
            share()));

    /**
     * Atualiza uma empresa.
     * @method updateCompany
     */
    updateCompany$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.updateCompany),
            withLatestFrom(this.store.select((state) => state.userProfile)),
            switchMap(([action, store]: [any, any]) => {
                return this.userService.updatePatchCompany(action.payload)
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: UserProfileActions.updateCompanyError,
                                    payload: { type: ErrorsType.Warning, value: result.messages },
                                };
                            }
                            if (result.hasError) {
                                return {
                                    type: UserProfileActions.updateCompanyError,
                                    payload: { type: ErrorsType.Warning, value: result.messages },
                                };
                            }
                            if (result.hasValue) {
                                return { type: UserProfileActions.updateCompanySuccess, payload: result.value };
                            }

                            if (result.ok) {
                                return { type: UserProfileActions.updateCompanySuccess, payload: { ...store.user, ...action.payload.value } };
                            }

                            return { type: UserProfileActions.updateCompanySuccess, payload: result };

                        }),
                        catchError((error) => of({ type: UserProfileActions.updateCompanyError, payload: error })));
            }),
            share()));

    /**
     * Atualiza um usuário, mas que nescessesita de aprovação.
     * @method lazyUpdateUser
     */
    lazyUpdateUser$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.lazyUpdate),
            withLatestFrom(this.store.select((state) => state.userProfile)),
            switchMap(([action, store]: [any, any]) => {
                return this.userService.updateUser({ step: action.payload.step, value: action.payload.value })
                    .pipe(
                        map((result) => {
                            if (result.ok || result.hasValue) {
                                const payload = result.hasValue ? result.value : { ...store.user, ...action.payload.value };
                                return { type: UserProfileActions.lazyUpdateSuccess, payload };
                            }

                            if (result.hasError) {
                                return { type: UserProfileActions.updateError, payload: { type: ErrorsType.Error, value: result.messages } };
                            }

                            if (result.hasAlerta || result.hasWarning) {
                                return { type: UserProfileActions.updateError, payload: { type: ErrorsType.Warning, value: result.messages } };
                            }

                            return { type: UserProfileActions.lazyUpdateSuccess, payload: result };

                        }),
                        catchError((error) => of({ type: UserProfileActions.updateError, payload: error })));
            }),
            share()));

    sendRegistration$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.sendRegistration),
            withLatestFrom(this.store.select((state) => state.authentication.currentUser)),
            filter(([action, currentUser]) => (action as any).payload && currentUser),
            tap(([, currentuser]) => {
                this.tracking.alias(currentuser.userId);
            })), { dispatch: false });

    /**
     * Obtem um usuário em uma determinada rota/{{STEP}}.
     * @method getUser
     */
    getUser$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.getUser),
            withLatestFrom(
                this.store.select((state) => state.userProfile),
                this.store.select((state) => state.authentication.currentUser),
            ),
            exhaustMap(([, userProfileState, currentUser]) => {
                if (userProfileState.pending === true) return of({ type: UserProfileActions.getttingUser });

                this.store.dispatch({ type: UserProfileActions.getttingUser });

                return this.userService.getUser()
                    .pipe(
                        switchMap((result) => {
                            if (!result.hasValue) {
                                return of({ type: UserProfileActions.getUserError, payload: result.messages });
                            }

                            const identity = withDerivedPersonType(result.value);
                            const eventsObj = {
                                name: identity.name,
                                email: identity.email,
                                cpfCnpj: identity.cpfcnpj,
                                phone: identity.phoneAreaCode + identity.phoneNumber,
                            };

                            this.userService.cacheUserProfileDataIfNeeded(identity);

                            this.store.dispatch({
                                type: TrackingActions.sendTrackLogin,
                                payload: { userId: identity.id, events: eventsObj },
                            });

                            return this.userService.getUserByCBLC()
                                .pipe(
                                    retry(2),
                                    map((cblcResult: any) => {
                                        const holder = cblcResult && cblcResult.hasValue
                                            ? withDerivedPersonType(cblcResult.value)
                                            : null;
                                        const actingDoc = String((currentUser && currentUser.identifier) || identity.cpfcnpj || '').replace(/\D/g, '');
                                        const holderDoc = String(holder && holder.cpfcnpj || '').replace(/\D/g, '');
                                        const normalizeName = (value: string) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
                                        const actingName = normalizeName(identity.name);
                                        const holderName = normalizeName(getProfileDisplayName(holder));
                                        const bothDocsPresent = !!actingDoc && !!holderDoc;
                                        const isRepresentation = bothDocsPresent
                                            ? holderDoc !== actingDoc
                                            : (!!actingName && !!holderName && actingName !== holderName);

                                        if (!isRepresentation) {
                                            return { type: UserProfileActions.getUserSuccess, payload: identity };
                                        }

                                        const representedProfile: any = {
                                            ...holder,
                                            isRepresentation: true,
                                            representativeName: identity.name,
                                        };

                                        return { type: UserProfileActions.getUserSuccess, payload: representedProfile };
                                    }),
                                    catchError(() => {
                                        return of({ type: UserProfileActions.getUserSuccess, payload: identity });
                                    }),
                                );
                        }),
                        catchError((error) => {
                            return of({ type: UserProfileActions.getUserError, payload: error });
                        }));
            }),
            share()));

    /**
     * Carrega o titular da conta pelo CBLC (usado em /saque e deep links).
     * Reaproveita o servico e regras de PF/PJ; escreve um unico estado via getUserSuccess.
     * @method getUserByCblc
     */
    getUserByCBLC$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.getUserByCBLC),
            exhaustMap(() => {
                return this.userService.getUserByCBLC()
                    .pipe(
                        map((result: any) => {
                            if (result && result.hasValue) {
                                return { type: UserProfileActions.getUserSuccess, payload: withDerivedPersonType(result.value) };
                            }
                            return { type: UserProfileActions.getUserError, payload: result && result.messages };
                        }),
                        catchError((error) => of({ type: UserProfileActions.getUserError, payload: error })),
                    );
            }),
            share()));

     /**
     * Obtem o cliente logado chamando a nova api escalável em lambda
     * @method getUserToroUser
     */
        getUserToroUser$ = createEffect(() => this.actions$
            .pipe(
                ofType(UserProfileActions.getUserToroUser),
                withLatestFrom(
                    this.store.select((state) => state.authentication.currentUser),
                ),
                exhaustMap(([, currentUser]) => {

                    return this.userService.getUserToroUser()
                    .pipe(
                        switchMap((result) => {
                            if (!result.hasValue) {
                                return of({ type: UserProfileActions.getUserError, payload: result.messages });
                            }

                            const identity = withDerivedPersonType(result.value);
                            const eventsObj = {
                                name: identity.name,
                                email: identity.email,
                                cpfCnpj: identity.cpfcnpj,
                                phone: identity.phoneAreaCode + identity.phoneNumber,
                            };
                            this.userService.cacheUserProfileDataIfNeeded(identity);

                            this.store.dispatch({
                                type: TrackingActions.sendTrackLogin,
                                payload: { userId: identity.id, events: eventsObj },
                            });

                            return this.userService.getUserByCBLC()
                                .pipe(
                                    retry(2),
                                    map((cblcResult: any) => {
                                        const holder = cblcResult && cblcResult.hasValue
                                            ? withDerivedPersonType(cblcResult.value)
                                            : null;
                                        const actingDoc = String((currentUser && currentUser.identifier) || identity.cpfcnpj || '').replace(/\D/g, '');
                                        const holderDoc = String(holder && holder.cpfcnpj || '').replace(/\D/g, '');
                                        const normalizeName = (value: string) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
                                        const actingName = normalizeName(identity.name);
                                        const holderName = normalizeName(getProfileDisplayName(holder));
                                        const bothDocsPresent = !!actingDoc && !!holderDoc;
                                        const isRepresentation = bothDocsPresent
                                            ? holderDoc !== actingDoc
                                            : (!!actingName && !!holderName && actingName !== holderName);

                                        if (!isRepresentation) {
                                            return { type: UserProfileActions.getUserSuccess, payload: identity };
                                        }

                                        const representedProfile: any = {
                                            ...holder,
                                            isRepresentation: true,
                                            representativeName: identity.name,
                                        };

                                        return { type: UserProfileActions.getUserSuccess, payload: representedProfile };
                                    }),
                                    catchError(() => {
                                        return of({ type: UserProfileActions.getUserSuccess, payload: identity });
                                    }),
                                );
                        }),
                        catchError((error) => of({ type: UserProfileActions.getUserError, payload: error })));
                }),
                share()
            ));


    ///
    getUserProfileData$ = createEffect(() => this.actions$
            .pipe(
                ofType(UserProfileActions.getCompany, UserProfileActions.getUserSuccess),
                debounceTime(500),
                tap(() => {
                    this.store.dispatch({ type: SuitabilityAction.getProfile });
                }),
                share()
            ),
            { dispatch: false } 
    );

    /**
     * Obtem uma empresa em uma determinada rota/{{STEP}}.
     * @method getCompany
     */
    getCompany$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.getCompany),
            switchMap(() => {
                return this.userService.getCompany()
                    .pipe(
                        map((result) => {
                            if (result.hasValue) {
                                return { type: UserProfileActions.getCompanySuccess, payload: result.value };
                            }
                            if (result.hasAlerta) {
                                return { type: UserProfileActions.getCompanyError, payload: result.messages };
                            }

                            return { type: UserProfileActions.getCompanyError, payload: result.messages };
                        }),
                        catchError((error) => of({ type: UserProfileActions.getCompanyError, payload: error })));
            }),
            share()));

    /** Recupera o modelo de risco do usuário */
    getRiskModel$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.getRiskModel),
            switchMap(() => {
                return this.backendService.get<DefaultResponse>(RoutesTable.posRisk.routes.getRiskModel.uri)
                    .pipe(
                        map((response) => {

                            if (response.ok) {
                                return { type: UserProfileActions.getRiskModelSuccess, payload: response.value };
                            }

                            return { type: UserProfileActions.getRiskModelError, payload: response.value };
                        }),
                        catchError((error) => of({ type: UserProfileActions.getRiskModelError, payload: error })));
            }),
            share()));

    /** Recupera se o usuário pode trocar modelo de garantia ou não */
    getGarantyModel$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.getGarantyModel),
            switchMap((action: any) => {

                const payload = {
                    riskModel: action.payload,
                };

                return this.backendService.get<DefaultResponse>(
                    RoutesTable.composeUri(RoutesTable.posRisk.routes.getGarantyModel, payload.riskModel),
                )
                    .pipe(
                        map((response) => {
                            if (response.ok) {
                                return { type: UserProfileActions.getGarantyModelSuccess, payload: response.value };
                            }
                            return { type: UserProfileActions.getGarantyModelError, payload: response.value };
                        }),
                        catchError((error) => of({ type: UserProfileActions.getGarantyModelError, payload: error })));
            }),
            share()));

    /** Atualiza o modelo do risco */
    updateRiskModel$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.updateRiskModel),
            switchMap((action: any) => {

                const payload = {
                    riskModel: action.payload,
                };

                return this.backendService
                    .post<DefaultResponse>(
                        RoutesTable.posRisk.routes.updateRiskModel.uri,
                        payload,
                        action.token,
                    )
                    .pipe(
                        map((result) => {
                            if (!result.ok) {
                                return { type: UserProfileActions.updateRiskModelError, payload: result.value };
                            }

                            return { type: UserProfileActions.updateRiskModelSuccess, payload: action.payload };
                        }),
                        catchError((error) => of({
                            type: UserProfileActions.updateRiskModelError,
                            payload: error,
                        })));
            }),
            share()));

    /**
     * Atualiza o celular do cliente
     */
    updatePhone$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.updatePhone),
            switchMap((action: any) => {

                return this.backendService
                    .post<DefaultResponse>(
                        RoutesTable.userprofile.routes.updatePhone.uri,
                        action.payload,
                        action.token,
                    )
                    .pipe(
                        map((result) => {

                            if (!result || !result.ok) {
                                return {
                                    type: UserProfileActions.updatePhoneError,
                                    payload: { type: ErrorsType.Error, value: result ? result.messages : [] },
                                };
                            }

                            return { type: UserProfileActions.updatePhoneSuccess, payload: result.ok };
                        }),
                        catchError((error) =>
                           of({ type: UserProfileActions.updatePhoneError, payload: { type: ErrorsType.Error, value: error } }),
                        ));
            }),
            share()));

    /**
     * Requisita rota para notificar sobre a origem do cadastro
     */
    notifyConclusion$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserProfileActions.notifyConclusion),
            switchMap((action: any) => {
                const url = RoutesTable.composeUri(RoutesTable.userprofile.routes.notifyConclusionToroUser, action.payload);
                return this.backendService.post<DefaultResponse>(url, null)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: UserProfileActions.notifyConclusionSuccess, payload: result.ok };
                            }
                            return { type: UserProfileActions.notifyConclusionError, payload: result.messages };
                        }),
                        catchError((error) =>
                            of({ type: UserProfileActions.notifyConclusionError, payload: error }),
                        ));
            }),
            share()));

    /**
     * Busca Perfil Trader do Usuário.
     * @method getTrader
     */
     getTrader$ = createEffect(() => this.actions$
         .pipe(
             ofType(UserProfileActions.getTrader),
             switchMap(() => {
                return this.newBackendService.api.get<DefaultResponse>('/trader')
                    .pipe(
                        map((result) => {
                            if (result) {
                                return { type: UserProfileActions.getTraderSuccess, payload: result };
                            }
                            return undefined;
                        }),
                        catchError((error) =>
                            of({ type: UserProfileActions.getTraderError, payload: error }),
                        ));
             }),
             share()));

    constructor(
        private store: Store<AppState>,
        private actions$: Actions,
        private userService: UserProfileService,
        private backendService: BackendService,
        private ngzone: NgZone,
        private tracking: TrackingService,
        private newBackendService: NewBackendService
    ) { }
}
